'use client';

import Head from 'next/head';

import { useIsomorphicLayoutEffect } from 'framer-motion';
import { useSessionStorage } from 'usehooks-ts';

import { type InteractiveProps } from './interactive';

export const CACHE_NAME = 'aapb-video-cache';

export async function fetchAndCacheVideo(videoUrl: string, cache: Cache, signal?: AbortSignal) {
  const cacheResponse = await cache.match(videoUrl);
  if (cacheResponse) {
    return cacheResponse;
  }
  const networkResponse = await fetchVideo(videoUrl, signal);
  // if the fetch was aborted, don't cache the response.
  if (!signal?.aborted) {
    await cache.put(videoUrl, networkResponse.clone());
  }
  return networkResponse;
}

export async function fetchVideo(videoUrl: string, signal?: AbortSignal) {
  return await fetch(videoUrl, { signal });
}

export function InteractivePreload({ experienceBlock }: InteractiveProps) {
  const media = experienceBlock.referencesCollection?.items || [];
  const [blobUrls, setBlobUrls] = useSessionStorage<Record<string, string>>(CACHE_NAME, {});

  useIsomorphicLayoutEffect(() => {
    if (!('caches' in self)) {
      return;
    }
    const abortController = new AbortController();
    (async () => {
      const cache = await caches.open(CACHE_NAME);
      await Promise.all(
        media.map(async item => {
          const videoItem = item && 'video' in item ? item : null;
          const src = videoItem?.video?.url;
          if (!src) {
            return;
          }
          const networkResponse = await fetchAndCacheVideo(src, cache, abortController.signal);
          const blob = await networkResponse.blob();
          const blobUrl = URL.createObjectURL(blob);
          setBlobUrls(urls => ({ ...urls, [src]: blobUrl }));
        }),
      );
    })();
    return () => {
      abortController.abort('unmounting InteractivePreload');
      Object.values(blobUrls).forEach(URL.revokeObjectURL);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media, setBlobUrls]);

  return typeof window !== 'undefined' && 'caches' in self ? (
    <Head>
      {media.map((reference, index) => {
        const videoItem = reference && 'video' in reference ? reference : null;
        return (
          <link
            rel="preload"
            as="fetch"
            href={`${videoItem?.video?.url}`}
            crossOrigin="anonymous"
            key={index}
          />
        );
      })}
    </Head>
  ) : undefined;
}
